// «¿Cerramos con X?» (diseño §14.1): el progreso, qué va a pasar, los avisos (pregunta sin contestar, está
// trabajando) y tres salidas: Cerrar bien · Todavía no · Despedir ahora sin recoger (doble confirmación).
import { useState } from 'react';
import { t } from '../../../i18n';
import { oficinaApi } from '../api';
import { mensajeError } from '../errores';
import type { ContratadoDTO, ExpedienteDTO } from '../compartido/vocabulario';
import { Aviso, Boton, Ventana } from '../ui';
import { quien } from '../marco/estado';

export function DialogoCerrar({ contratado: c, expediente: e, abierto, alCerrar }: { contratado: ContratadoDTO; expediente: ExpedienteDTO | undefined; abierto: boolean; alCerrar: () => void }) {
  const [enviando, setEnviando] = useState(false);
  const [despedir, setDespedir] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hechos = e?.criterios.filter((k) => k.estado === 'cumplido').length ?? 0;
  const total = e?.criterios.length ?? 0;

  async function hacer(accion: () => Promise<unknown>) {
    setEnviando(true);
    setError(null);
    try {
      await accion();
      setDespedir(false);
      alCerrar();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  const pie = despedir ? (
    <>
      <Boton className="mr-auto" onClick={() => setDespedir(false)}>
        {t('oficinaV2.accion.cancelar')}
      </Boton>
      <Boton variante="peligro" cargando={enviando} onClick={() => void hacer(() => oficinaApi.despedir(c.agentId))}>
        {t('oficinaV2.cierre.despedirSi')}
      </Boton>
    </>
  ) : (
    <>
      <Boton variante="fantasma" className="mr-auto text-oficina-error hover:bg-oficina-error/10" onClick={() => setDespedir(true)}>
        {t('oficinaV2.cierre.despedir')}
      </Boton>
      <Boton onClick={alCerrar}>{t('oficinaV2.cierre.todaviaNo')}</Boton>
      <Boton variante="primario" cargando={enviando} onClick={() => void hacer(() => oficinaApi.cerrar(c.agentId))}>
        {t('oficinaV2.cierre.cerrarBien')}
      </Boton>
    </>
  );

  return (
    <Ventana
      abierta={abierto}
      alCerrar={() => {
        setDespedir(false);
        alCerrar();
      }}
      titulo={t('oficinaV2.cierre.titulo', { nombre: quien(c) })}
      descripcion={total ? t('oficinaV2.cierre.progreso', { hechos, total }) : undefined}
      pie={pie}
    >
      {despedir ? (
        <Aviso tono="error">{t('oficinaV2.cierre.despedirConfirma')}</Aviso>
      ) : (
        <div className="space-y-4 pb-1 text-o-cuerpo leading-relaxed text-oficina-texto">
          <Paso n={1} texto={t('oficinaV2.cierre.paso1', { nombre: quien(c) })} />
          <Paso n={2} texto={t('oficinaV2.cierre.paso2')} />
          <Paso n={3} texto={t(c.tipoTrabajo === 'investigar' ? 'oficinaV2.cierre.paso3Investigar' : 'oficinaV2.cierre.paso3')} />
          <p className="text-o-texto text-oficina-texto3">{t('oficinaV2.cierre.tarda')}</p>
          {e?.pregunta ? <Aviso tono="aviso">{t('oficinaV2.cierre.avisoPregunta', { pregunta: e.pregunta.pregunta })}</Aviso> : null}
          {c.visual === 'trabajando' ? <Aviso>{t('oficinaV2.cierre.avisoTrabajando')}</Aviso> : null}
        </div>
      )}
      {error ? (
        <div className="mt-3">
          <Aviso tono="error">{error}</Aviso>
        </div>
      ) : null}
    </Ventana>
  );
}

function Paso({ n, texto }: { n: number; texto: string }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-oficina-acento/20 text-o-nota font-semibold text-oficina-enlace">{n}</span>
      <p className="pt-0.5 text-oficina-texto2">{texto}</p>
    </div>
  );
}
